import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API, Endpoints, PageModel, Tag } from "@brg/api";
import { FlexBox, useBreakpoint } from "@brg/ui";
import { useTranslation } from "react-i18next";
import { AppTester } from "supporting";
import styled, { css } from "styled-components";
import { CollapseButton } from "shared/components/CollapseButton/CollapseButton";
import { TagsTitleWrapper } from "shared/pages/designer/components/DesignerFolders/components/designerTagsCloud";
import { useGetTagsByIds } from "shared/hooks/api/useGetTagsByIds";
import { bTag, LayoutConst } from "core";
import useQueryState from "shared/hooks/useQueryState";
import { BTagsGridNew } from "core/components/bTag/bTagsGridNew/bTagsGridNew";
import { useDeepCompareEffect } from "shared/utils/useDeepCompareEffect";
import { useDispatch, useSelector } from "react-redux";
import { fetchFeedTags, fetchMoreFeedTags, getFeedTags } from "shared/reducers/FeedTagsSlice";
import { BTag } from "core/components";

export const TagWrapper = styled.div<{isLoading?: boolean, isCompetition?: boolean, isGeo?: boolean, isCGRAVE?: boolean, isAdcrDafesNet?: boolean}>`
  background: ${({theme, isCompetition, isGeo, isCGRAVE, isAdcrDafesNet}) => isCGRAVE || isAdcrDafesNet ? '#2C2C2B' : !isCompetition ? theme.tags.cloud.background : isGeo ? '#353A3E' : '#464645'};
  // padding: 16px 16px 4px 16px;
  padding: 16px 24px;
  width: 100%;
  box-sizing: border-box;  
  border-radius: 3px;
  position: relative;
  height: ${({isLoading}) => isLoading ? '102px' : 'auto'};

  &:not(:first-child) {
    margin-top: 16px;
  }

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    height: ${({isLoading}) => isLoading ? '98px' : 'auto'};
  }
  
  @media (max-width: ${LayoutConst.screen.m}px) {
    margin-bottom: 16px;
    margin-top: 0;
    height: ${({isLoading}) => isLoading ? '78px' : 'auto'};
  }
`

export const TagWrapperLocal = styled(TagWrapper)`

  #tags_grid_wrapper {
    &::-webkit-scrollbar {
      width: 2px;
    }
    &::-webkit-scrollbar-track {
      border-radius: 10px;
      background-color: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background-color: transparent;
    }
  }

  @media (max-width: ${LayoutConst.screen.m}px) {
    margin-bottom: 0;
    &:not(:first-child) {
      margin-top: 16px;
    }
  }
`;

export const MoreButton = styled.div`
  cursor: pointer;
  place-items: center;
  margin-bottom: 12px;
  margin-top: 2px;
  color: rgba(144, 144, 144, 1);
  font-size: 15px;
  line-height: 20px;
  margin-bottom: 8px;
`;

export const ScrollWrapper = styled.div<{isLoading?: boolean, collapse?: boolean, hideBottomLine?: boolean, overflowVisible?: boolean, isAdcrDafesNet?: boolean }>`
  max-height: ${({ collapse }) => !collapse ? 'none' : '120px'};
  overflow-y: hidden;
  margin: 0 0 -10px 0;
  margin: 0;
  width: 100%;
  display: inline-flex;
  flex-wrap: wrap;
  position: relative;
  
  // @media (max-width: ${LayoutConst.screen.mac13}px) {
  //   max-height: ${({ collapse }) => collapse ? '85px' : 'none'};
  // }
  
  // @media (max-width: ${LayoutConst.screen.m}px) {
  //   max-height: ${({ collapse }) => collapse ? '80px' : 'none'};
  // }

  // @media (max-width: ${LayoutConst.screen.s - 1}px) {
  //   max-height: ${({ collapse }) => collapse ? '80px' : 'none'};
  // }

  &::-webkit-scrollbar {
    width: 2px;
  }
  &::-webkit-scrollbar-track {
    border-radius: 10px;
    background-color: #F2F2F2;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #909090;
  }


`

export const FeedCloud: FC<{accountId: string, onUpdate?: () => void }> = ({ accountId, onUpdate }) => {
  const tagsRef = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();
  const [{ tab, tagId: queryTag, sorting }, setQueryState] = useQueryState();
  const [open, setOpen] = useState(false);
  const isMac13 = useBreakpoint(LayoutConst.screen.mac13);
  const [collapseVisible, setCollapseVisible] = useState(false);
  const { tags, loading } = useSelector(getFeedTags);
  const dispatch = useDispatch();

  const data = useMemo(() => API.toLang<Tag[]>(tags.data, i18n.language) , [tags])

  const moreButtonVisible = useMemo(() => {
    //@ts-ignore
    return tags && tags?.paging?.page < tags?.paging?.pages
  } , [tags])

  const isGeo = AppTester.isGeograffee() && !AppTester.isGeoDafes();
  const isCGRAVE = AppTester.isCGRAVE();
  const isAdcrDafesNet = AppTester.isAdcrDafesNet();
  const isContinuumSite = AppTester.isContinuumSite();

  const smHeight = useMemo(() => (window.innerHeight < LayoutConst.screen.height.projectCover), [])

  const maxTagHeight = (isMac13 || smHeight) ? 88 : 92;

  const onTagClick = useCallback((tagId: string, isHidden?: boolean) => {
    setQueryState({ tagId: queryTag === tagId ? undefined : tagId, sorting }, { replace: true })
    if (queryTag === tagId) {
      onUpdate?.()
    }
  }, [queryTag, tab, sorting, onUpdate])

  const filter = useMemo(() => {
    return {
      page: 1,
      limit: 20,
      accountId
    }
  } , [accountId])

  const onFetchMoreTags = () => {
    dispatch(fetchMoreFeedTags({ 
        accountId, 
        page: tags?.paging?.page + 1, 
        limit: 20, 
        offset: tags?.paging?.offset + 20 
    }))
    setOpen(true)
  }

  useDeepCompareEffect(() => {
    if (filter) {
        dispatch(fetchFeedTags(filter))
    }
  }, [filter])

  useEffect(() => {
    setTimeout(() => {
      const tagsContainer = document.querySelector('#tags-scroll-container');
      setCollapseVisible(false);

      if (tagsContainer) {
        if (tagsContainer.scrollHeight > maxTagHeight) {
          setCollapseVisible(true)
        }
      }
    } , 100)
       
  },[loading, maxTagHeight, ]);

  const moreButton = useMemo(() => {
    if (!moreButtonVisible) return null;

    return <MoreButton onClick={() => onFetchMoreTags()}>{t('more')}</MoreButton>
  } , [open, collapseVisible, moreButtonVisible])

  const toggleButton = useMemo(() => {
    if (!moreButtonVisible && !!open) {
      return (
        <CollapseButton flag={collapseVisible} onClick={() => {
          setCollapseVisible(!collapseVisible)
        }}
        />
      )
    }
  } , [moreButtonVisible, open, collapseVisible])

  if (loading && !data?.length) return <TagWrapper isLoading isGeo={isGeo}/>

  if (!data?.length) return null;

  return (
    <TagWrapperLocal isCGRAVE={isCGRAVE || isContinuumSite || isAdcrDafesNet}>
    <FlexBox>
        <FlexBox direction="column" directionMobile="column" gy={12} width='100%'>
            <ScrollWrapper id={'tags-scroll-container'} ref={tagsRef} collapse={!collapseVisible} isAdcrDafesNet={isAdcrDafesNet} >
                <BTagsGridNew style={{marginBottom: '12px'}} noLimitMaxHeight>
                  {data?.map((tag: Tag) => <BTag
                      tag={tag}
                      onClick={() => onTagClick(tag.tagId)}
                      key={tag.tagId}
                      selected={tag.tagId === queryTag}
                      // @ts-ignore
                      educational={tag?.isEducationTag}
                      // @ts-ignore
                      organization={tag?.isTeamTag}
                      isShort
                      isTagsCloud
                  >
                      <TagsTitleWrapper gx={8}><span>{bTag.title(tag)}</span></TagsTitleWrapper>
                  </BTag>)}
                </BTagsGridNew>
            </ScrollWrapper>
        </FlexBox>
    </FlexBox>
    {toggleButton}
    {moreButton}
  </TagWrapperLocal>
  )
}import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API, Endpoints, PageModel, Tag } from "@brg/api";
import { FlexBox, useBreakpoint } from "@brg/ui";
import { useTranslation } from "react-i18next";
import { AppTester } from "supporting";
import styled, { css } from "styled-components";
import { CollapseButton } from "shared/components/CollapseButton/CollapseButton";
import { TagsTitleWrapper } from "shared/pages/designer/components/DesignerFolders/components/designerTagsCloud";
import { useGetTagsByIds } from "shared/hooks/api/useGetTagsByIds";
import { bTag, LayoutConst } from "core";
import useQueryState from "shared/hooks/useQueryState";
import { BTagsGridNew } from "core/components/bTag/bTagsGridNew/bTagsGridNew";
import { useDeepCompareEffect } from "shared/utils/useDeepCompareEffect";
import { useDispatch, useSelector } from "react-redux";
import { fetchFeedTags, fetchMoreFeedTags, getFeedTags } from "shared/reducers/FeedTagsSlice";
import { BTag } from "core/components";

export const TagWrapper = styled.div<{isLoading?: boolean, isCompetition?: boolean, isGeo?: boolean, isCGRAVE?: boolean, isAdcrDafesNet?: boolean}>`
  background: ${({theme, isCompetition, isGeo, isCGRAVE, isAdcrDafesNet}) => isCGRAVE || isAdcrDafesNet ? '#2C2C2B' : !isCompetition ? theme.tags.cloud.background : isGeo ? '#353A3E' : '#464645'};
  // padding: 16px 16px 4px 16px;
  padding: 16px 24px;
  width: 100%;
  box-sizing: border-box;  
  border-radius: 3px;
  position: relative;
  height: ${({isLoading}) => isLoading ? '102px' : 'auto'};

  &:not(:first-child) {
    margin-top: 16px;
  }

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    height: ${({isLoading}) => isLoading ? '98px' : 'auto'};
  }
  
  @media (max-width: ${LayoutConst.screen.m}px) {
    margin-bottom: 16px;
    margin-top: 0;
    height: ${({isLoading}) => isLoading ? '78px' : 'auto'};
  }
`

export const TagWrapperLocal = styled(TagWrapper)`

  #tags_grid_wrapper {
    &::-webkit-scrollbar {
      width: 2px;
    }
    &::-webkit-scrollbar-track {
      border-radius: 10px;
      background-color: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background-color: transparent;
    }
  }

  @media (max-width: ${LayoutConst.screen.m}px) {
    margin-bottom: 0;
    &:not(:first-child) {
      margin-top: 16px;
    }
  }
`;

export const MoreButton = styled.div`
  cursor: pointer;
  place-items: center;
  margin-bottom: 12px;
  margin-top: 2px;
  color: rgba(144, 144, 144, 1);
  font-size: 15px;
  line-height: 20px;
  margin-bottom: 8px;
`;

export const ScrollWrapper = styled.div<{isLoading?: boolean, collapse?: boolean, hideBottomLine?: boolean, overflowVisible?: boolean, isAdcrDafesNet?: boolean }>`
  max-height: ${({ collapse }) => !collapse ? 'none' : '120px'};
  overflow-y: hidden;
  margin: 0 0 -10px 0;
  margin: 0;
  width: 100%;
  display: inline-flex;
  flex-wrap: wrap;
  position: relative;
  
  // @media (max-width: ${LayoutConst.screen.mac13}px) {
  //   max-height: ${({ collapse }) => collapse ? '85px' : 'none'};
  // }
  
  // @media (max-width: ${LayoutConst.screen.m}px) {
  //   max-height: ${({ collapse }) => collapse ? '80px' : 'none'};
  // }

  // @media (max-width: ${LayoutConst.screen.s - 1}px) {
  //   max-height: ${({ collapse }) => collapse ? '80px' : 'none'};
  // }

  &::-webkit-scrollbar {
    width: 2px;
  }
  &::-webkit-scrollbar-track {
    border-radius: 10px;
    background-color: #F2F2F2;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #909090;
  }


`

export const FeedCloud: FC<{accountId: string, onUpdate?: () => void }> = ({ accountId, onUpdate }) => {
  const tagsRef = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();
  const [{ tab, tagId: queryTag, sorting }, setQueryState] = useQueryState();
  const [open, setOpen] = useState(false);
  const isMac13 = useBreakpoint(LayoutConst.screen.mac13);
  const [collapseVisible, setCollapseVisible] = useState(false);
  const { tags, loading } = useSelector(getFeedTags);
  const dispatch = useDispatch();

  const data = useMemo(() => API.toLang<Tag[]>(tags.data, i18n.language) , [tags])

  const moreButtonVisible = useMemo(() => {
    //@ts-ignore
    return tags && tags?.paging?.page < tags?.paging?.pages
  } , [tags])

  const isGeo = AppTester.isGeograffee() && !AppTester.isGeoDafes();
  const isCGRAVE = AppTester.isCGRAVE();
  const isAdcrDafesNet = AppTester.isAdcrDafesNet();
  const isContinuumSite = AppTester.isContinuumSite();

  const smHeight = useMemo(() => (window.innerHeight < LayoutConst.screen.height.projectCover), [])

  const maxTagHeight = (isMac13 || smHeight) ? 88 : 92;

  const onTagClick = useCallback((tagId: string, isHidden?: boolean) => {
    setQueryState({ tagId: queryTag === tagId ? undefined : tagId, sorting }, { replace: true })
    if (queryTag === tagId) {
      onUpdate?.()
    }
  }, [queryTag, tab, sorting, onUpdate])

  const filter = useMemo(() => {
    return {
      page: 1,
      limit: 20,
      accountId
    }
  } , [accountId])

  const onFetchMoreTags = () => {
    dispatch(fetchMoreFeedTags({ 
        accountId, 
        page: tags?.paging?.page + 1, 
        limit: 20, 
        offset: tags?.paging?.offset + 20 
    }))
    setOpen(true)
  }

  useDeepCompareEffect(() => {
    if (filter) {
        dispatch(fetchFeedTags(filter))
    }
  }, [filter])

  useEffect(() => {
    setTimeout(() => {
      const tagsContainer = document.querySelector('#tags-scroll-container');
      setCollapseVisible(false);

      if (tagsContainer) {
        if (tagsContainer.scrollHeight > maxTagHeight) {
          setCollapseVisible(true)
        }
      }
    } , 100)
       
  },[loading, maxTagHeight, ]);

  const moreButton = useMemo(() => {
    if (!moreButtonVisible) return null;

    return <MoreButton onClick={() => onFetchMoreTags()}>{t('more')}</MoreButton>
  } , [open, collapseVisible, moreButtonVisible])

  const toggleButton = useMemo(() => {
    if (!moreButtonVisible && !!open) {
      return (
        <CollapseButton flag={collapseVisible} onClick={() => {
          setCollapseVisible(!collapseVisible)
        }}
        />
      )
    }
  } , [moreButtonVisible, open, collapseVisible])

  if (loading && !data?.length) return <TagWrapper isLoading isGeo={isGeo}/>

  if (!data?.length) return null;

  return (
    <TagWrapperLocal isCGRAVE={isCGRAVE || isContinuumSite || isAdcrDafesNet}>
    <FlexBox>
        <FlexBox direction="column" directionMobile="column" gy={12} width='100%'>
            <ScrollWrapper id={'tags-scroll-container'} ref={tagsRef} collapse={!collapseVisible} isAdcrDafesNet={isAdcrDafesNet} >
                <BTagsGridNew style={{marginBottom: '12px'}} noLimitMaxHeight>
                  {data?.map((tag: Tag) => <BTag
                      tag={tag}
                      onClick={() => onTagClick(tag.tagId)}
                      key={tag.tagId}
                      selected={tag.tagId === queryTag}
                      // @ts-ignore
                      educational={tag?.isEducationTag}
                      // @ts-ignore
                      organization={tag?.isTeamTag}
                      isShort
                      isTagsCloud
                  >
                      <TagsTitleWrapper gx={8}><span>{bTag.title(tag)}</span></TagsTitleWrapper>
                  </BTag>)}
                </BTagsGridNew>
            </ScrollWrapper>
        </FlexBox>
    </FlexBox>
    {toggleButton}
    {moreButton}
  </TagWrapperLocal>
  )
}